import { useEffect, useState } from "react";
import { Lock } from "lucide-react";
import { api, pad, fmtDate } from "../lib/api";
import { useTier, PLAN_LABEL } from "../lib/tiers";
import { PoolTracker } from "../components/PoolTracker";
import { TierSwitch } from "../components/TierSwitch";

const PAGE = 100;

const Row = ({ r, tid }) => (
  <div data-testid={tid}
    className={`grid grid-cols-12 px-6 py-4 items-center border-b border-white/5 transition-colors hover:bg-[#283454] ${r.is_me ? "bg-[#38BDF8]/[0.08]" : ""}`}>
    <span className="col-span-3 font-mono-lux text-[#38BDF8]">{pad(r.position)}</span>
    <span className="col-span-5 flex items-center gap-3 flex-wrap">
      {r.is_me ? <span data-testid="queue-row-me" className="text-[#38BDF8] font-medium">You</span> : <span data-testid={`queue-alias-${r.position}`} className="text-slate-300">{r.alias}</span>}
      <span className="text-[10px] font-mono-lux uppercase text-slate-500">{PLAN_LABEL[r.plan]}</span>
      {r.level === "premium" && <span className="text-[10px] font-mono-lux uppercase text-[#F43F5E]">Premium</span>}
      {r.position === 1 && <span className="text-[10px] font-mono-lux uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#38BDF8]/15 text-[#38BDF8]">Up next</span>}
    </span>
    <span className="col-span-4 text-right text-sm text-slate-400 font-mono-lux">{fmtDate(r.joined_at)}</span>
  </div>
);

export default function QueuePage() {
  const [tier, setTier] = useTier();
  const [data, setData] = useState({ items: [], total: 0, me: null });
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => { api.get("/public/stats").then((r) => setStats(r.data)); }, []);
  useEffect(() => { api.get(`/queue?tier=${tier}&limit=${PAGE}`).then((r) => setData(r.data)); }, [tier]);
  const ts = stats?.[tier];
  const more = async () => {
    setLoading(true);
    const r = await api.get(`/queue?tier=${tier}&limit=${PAGE}&offset=${data.items.length}`).finally(() => setLoading(false));
    setData((d) => ({ ...r.data, items: [...d.items, ...r.data.items] }));
  };
  const me = data.items.some((r) => r.is_me) ? null : data.me;

  return (
    <div className="max-w-7xl mx-auto px-5 md:px-10 pt-32 pb-24 grid lg:grid-cols-12 gap-10">
      <div className="lg:col-span-8">
        <p className="eyebrow">Live Queue Board</p>
        <h1 className="font-serif-lux text-4xl sm:text-5xl mt-3">The Lines</h1>
        <p className="text-slate-400 mt-3 text-sm md:text-base flex items-center gap-2">
          <Lock className="w-4 h-4 text-[#38BDF8]" />Aliases only — real names are never shown. Your own spot is marked "You".
        </p>
        <TierSwitch value={tier} onChange={setTier} className="mt-8" />
        <p data-testid="queue-total" className="mt-6 text-xs font-mono-lux uppercase tracking-[0.2em] text-slate-500">
          Showing {data.items.length} of {data.total.toLocaleString()} in line
        </p>
        {me && <div className="mt-3 lux-card overflow-hidden" data-testid="queue-my-spot"><Row r={me} tid="queue-my-row" /></div>}
        <div className="mt-3 lux-card overflow-hidden" data-testid="queue-table">
          <div className="grid grid-cols-12 px-6 py-3 text-[10px] font-mono-lux tracking-[0.2em] uppercase text-slate-500 border-b border-white/5">
            <span className="col-span-3">Position</span><span className="col-span-5">Member</span><span className="col-span-4 text-right">In line since</span>
          </div>
          {data.items.map((r) => <Row key={r.position} r={r} tid={`queue-row-${r.position}`} />)}
          {data.items.length === 0 && <p className="px-6 py-10 text-slate-500 text-sm">This line is empty — be the first.</p>}
        </div>
        {data.items.length < data.total && (
          <button data-testid="queue-load-more-btn" disabled={loading} onClick={more} className="btn-ghost px-6 py-3 text-sm mt-5">
            {loading ? "Loading…" : `Load ${Math.min(PAGE, data.total - data.items.length)} more`}
          </button>
        )}
      </div>
      <aside className="lg:col-span-4 lg:pt-28 space-y-5">
        <PoolTracker stats={ts} compact />
        <div className="lux-card p-6 text-sm text-slate-400 space-y-3">
          <p className="eyebrow">Rules</p>
          <p>Each line has its own pool and order. You can be in several lines at once.</p>
          <p>Missed payment → removed from that line. Renewing after a lapse → back of the line.</p>
          <p>#001 gets {ts?.offer_days ?? 7} days to accept, pass (move back 3 spots) or donate the trip's value to trade-school scholarships.</p>
          <p>We also support trade-school students. <a href="/mission" className="text-[#38BDF8] hover:underline">See our mission</a>.</p>
        </div>
      </aside>
    </div>
  );
}
